import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Divider,
  Snackbar,
  Tooltip,
  IconButton,
  Grid,
  Stack
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import YAML from 'yaml';
import { formatCPUToCores, formatMemoryToMB } from '../../../utils/formatNumber';

/* ===============================
   MAIN COMPONENT
================================ */

export default function VmDetailModal({
  open,
  onClose,
  data,
  title = 'VM Configuration',
  variant = 'vm'
}) {
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(YAML.stringify(data, null, 2));
    setCopied(true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Detail" />
        <Tab label="YAML View" />
      </Tabs>

      {/* Content */}
      <DialogContent dividers>
        <Box sx={{ maxHeight: 700, overflowY: 'auto' }}>
          {tab === 0 && <BeautyView data={data} variant={variant} />}

          {tab === 1 && (
            <Box sx={{ position: 'relative' }}>
              <Tooltip title="Copy YAML">
                <IconButton
                  aria-label="copy yaml"
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                  onClick={handleCopy}
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <YamlView data={data} />
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Snackbar */}
      <Snackbar
        open={copied}
        autoHideDuration={1500}
        onClose={() => setCopied(false)}
        message="YAML copied!"
      />
    </Dialog>
  );
}

/* ===============================
   YAML VIEW
================================ */

function YamlView({ data }) {
  return (
    <Box
      component="pre"
      sx={{
        backgroundColor: '#0f172a',
        color: '#e5e7eb',
        p: 2,
        borderRadius: 1,
        fontSize: 13,
        minHeight: 650,
        overflow: 'auto'
      }}
    >
      {YAML.stringify(data, null, 2)}
    </Box>
  );
}

YamlView.propTypes = {
  data: PropTypes.object
};

/* ===============================
   BEAUTY VIEW
================================ */

function BeautyView({ data, variant }) {
  const isGateway = variant === 'gateway';
  const isVM = variant === 'vm';

  const formatTrunks = (trunks) => trunks?.join(', ');
  const formatResource = (v) => {
    if (!v || v === '0') return '--';
    return variant === 'vm' ? v.toUpperCase() : v;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 650 }}>
      {/* BASIC INFO */}
      <Grid container spacing={3} py={2}>
        {isGateway ? (
          <>
            <Grid item>
              <Stack spacing={1.5}>
                <Row label="VM Name" value={data.vm_name} />
                <Row label="Size" value={data.size} />
              </Stack>
            </Grid>
            <Grid item>
              <Stack spacing={1.5}>
                <Row label="Disk Size" value={data.diskSize} />
                <Row label="Persist Data" value={data.persistData ? 'Yes' : 'No'} />
              </Stack>
            </Grid>
            <Grid item>
              <Stack spacing={1.5}>
                <Row label="SSH Password Auth" value={data.sshPwAuth ? 'Enabled' : 'Disabled'} />
              </Stack>
            </Grid>
          </>
        ) : (
          <>
            <Grid item>
              <Stack spacing={1.5}>
                <Row label="Name" value={data.name} />
                <Row label="Replicas" value={data.replicas} />
              </Stack>
            </Grid>
            <Grid item>
              <Stack spacing={1.5}>
                <Row label="Architecture" value={data.architecture} />
                <Row label="Persist Data" value={data.persistData ? 'Yes' : 'No'} />
              </Stack>
            </Grid>
            <Grid item>
              <Stack>
                <Row label="Connectivity" value={data.connectivity ? 'Yes' : 'No'} />
              </Stack>
            </Grid>
          </>
        )}
      </Grid>

      {/* DISKS (vm only) */}
      {isVM && (
        <Section title="Disks">
          {data.disks?.map((disk, i) => (
            <DiskCard key={i} disk={disk} index={i} />
          ))}
        </Section>
      )}

      {/* NETWORK */}
      <Section title="Network Ports">
        {data.network?.ports?.map((port, i) => (
          <NetworkCard key={i} port={port} index={i} formatTrunks={formatTrunks} />
        ))}
      </Section>

      {/* Env Variables */}
      <Section title="Environment Variables">
        {data.env?.map((env, i) => (
          <EnvCard key={i} env={env} />
        ))}
      </Section>

      {/* RESOURCES */}
      <Section title={isGateway ? 'Gateway Resources' : 'VM Resources'}>
        <>
          <ResourceBlock
            title="CPU"
            request={
              isGateway
                ? formatResource(formatCPUToCores(data.gateway_resources?.cpu_requested))
                : formatResource(data.resources?.requests?.cpu)
            }
            limit={
              isGateway
                ? formatResource(formatCPUToCores(data.gateway_resources?.cpu_limit))
                : formatResource(data.resources?.limits?.cpu)
            }
          />

          <ResourceBlock
            title="Memory"
            request={
              isGateway
                ? formatResource(formatMemoryToMB(data.gateway_resources?.memory_requested))
                : formatResource(data.resources?.requests?.memory)
            }
            limit={
              isGateway
                ? formatResource(formatMemoryToMB(data.gateway_resources?.memory_limit))
                : formatResource(data.resources?.limits?.memory)
            }
          />
        </>
      </Section>

      {/* SCRIPTS */}
      <Section title="Init Script">
        <ScriptList scripts={data.initscript} />
      </Section>

      <Section title="Boot Script">
        <ScriptList scripts={data.bootscript} />
      </Section>
    </Box>
  );
}

BeautyView.propTypes = {
  data: PropTypes.object,
  variant: PropTypes.oneOf(['vm', 'gateway'])
};

/* ===============================
   SUB COMPONENTS
================================ */

function DiskCard({ disk, index }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        {index === 0 ? 'OS Disk' : 'Data Disk'}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Row label="Size" value={`${disk.diskSize} (${disk.size})`} />
        </Grid>

        {disk.image && (
          <Grid item xs={9}>
            <Row label="Image" value={disk.image} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function NetworkCard({ port, index, formatTrunks }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        Port {index + 1}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Row label="Name" value={port.name} />
        </Grid>

        {port.vlan_mode && (
          <Grid item xs={3}>
            <Row label="VLAN Mode" value={port.vlan_mode} />
          </Grid>
        )}

        {port.tag && (
          <Grid item xs={3}>
            <Row label="VLAN Tag" value={port.tag} />
          </Grid>
        )}

        {port.trunks && (
          <Grid item xs={3}>
            <Row label="Trunks" value={formatTrunks(port.trunks)} />
          </Grid>
        )}

        {port.bridge && (
          <Grid item xs={3}>
            <Row label="Bridge" value={port.bridge} />
          </Grid>
        )}

        {port.number && (
          <Grid item xs={3}>
            <Row label="Number" value={port.number} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function EnvCard({ env }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Row label="Name" value={`${env.name}`} />
        </Grid>
        <Grid item xs={9}>
          <Row label="Value" value={env.value} />
        </Grid>
      </Grid>
    </Box>
  );
}

function ResourceBlock({ title, request, limit }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Row label="Request" value={request} />
        </Grid>

        <Grid item xs={3}>
          <Row label="Limit" value={limit} />
        </Grid>
      </Grid>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={600}>
        {title}
      </Typography>

      <Divider sx={{ my: 1 }} />

      {children}
    </Box>
  );
}

function Row({ label, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        whiteSpace: 'nowrap',
        overflow: 'hidden'
      }}
    >
      {/* Label */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ flexShrink: 0 }}>
        {label}:
      </Typography>

      {/* Value */}
      <Typography
        variant="body2"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
        title={value}
      >
        {value || '-'}
      </Typography>
    </Box>
  );
}

function ScriptList({ scripts }) {
  return (
    <Box
      component="ul"
      sx={{
        backgroundColor: '#f8fafc',
        borderRadius: 1,
        pl: 3
      }}
    >
      {scripts?.map((line, i) => (
        <li key={i}>
          <Typography fontFamily="monospace" variant="body2">
            {line}
          </Typography>
        </li>
      ))}
    </Box>
  );
}

/* ===============================
   PROPTYPES
================================ */

VmDetailModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  data: PropTypes.object,
  title: PropTypes.string,
  variant: PropTypes.oneOf(['vm', 'gateway'])
};

DiskCard.propTypes = {
  disk: PropTypes.object,
  index: PropTypes.number
};

NetworkCard.propTypes = {
  port: PropTypes.object,
  index: PropTypes.number,
  formatTrunks: PropTypes.func
};

EnvCard.propTypes = {
  env: PropTypes.object
};

ResourceBlock.propTypes = {
  title: PropTypes.string,
  request: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  limit: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

Section.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node
};

Row.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
};

ScriptList.propTypes = {
  scripts: PropTypes.array
};
